import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Line from "../shipment/line";
import QRCode from "react-qr-code";
import A from "../shipments/a";
import clsx from "clsx";
import MapSelector from "../mapSelector/mapSelector";
import SwipeableViews from "react-swipeable-views";
import Barcode from "react-barcode";

const useStyles = makeStyles(theme => ({
    icon: {
        fontSize: '3em',
        color: 'black',
    },
    qrCodeContainer: {
        background: 'white',
        maxWidth: 200,
        padding: '16px',
        display: 'inline-block',
        cursor: 'pointer',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
            transform: 'scale(1.05)',
        }
    },
    qrCodeInner: {
        height: "auto",
        margin: "0 auto",
        width: "100%"
    },
    fullscreenOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(255, 255, 255, 1)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        zIndex: 10000,
        cursor: 'pointer',
        padding: '20px',
        paddingTop: '5vh',
        boxSizing: 'border-box',
        overflowY: 'auto',
    },
    fullscreenQrCodeWrapper: {
        background: 'white',
        padding: '20px',
        width: 'auto',
        height: 'auto',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    fullscreenHint: {
        marginTop: '20px',
        color: '#333',
        fontSize: '1rem',
        textAlign: 'center',
    }
}));

export default function ShowBox({ box, showPin, children }) {
    const classes = useStyles();
    const [isMapSelectorOpen, setIsMapSelectorOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState({ lat: null, lng: null });
    const [isQrFullscreen, setIsQrFullscreen] = useState(false);
    const wakeLockSentinelRef = useRef(null); // To store the wake lock

    const handleAddressClick = (addressData) => {
        if (addressData?.lat && addressData?.lng) {
            setSelectedLocation(addressData);
            setIsMapSelectorOpen(true);
        } else {
            console.warn("Address clicked, but latitude or longitude is missing.");
        }
    };

    const handleMapSelectorClose = () => {
        setIsMapSelectorOpen(false);
        setSelectedLocation({ lat: null, lng: null });
    };

    const handleQrClick = async () => {
        if (!box?.dailyPin) return;

        setIsQrFullscreen(true);
        if ('wakeLock' in navigator && !wakeLockSentinelRef.current) {
            try {
                wakeLockSentinelRef.current = await navigator.wakeLock.request('screen');
                console.log('Screen wake lock acquired.');
                wakeLockSentinelRef.current.addEventListener('release', () => {
                    console.log('Screen wake lock was released.');
                    wakeLockSentinelRef.current = null; // Clear the ref if released externally
                });
            } catch (err) {
                console.error(`Wake Lock error: ${err.name}, ${err.message}`);
                wakeLockSentinelRef.current = null;
            }
        } else if (!('wakeLock' in navigator)) {
            console.warn('Wake Lock API not supported.');
        } else {
            console.log('Wake lock already held or request pending.');
        }
    };

    const handleCloseFullscreenQr = async () => {
        setIsQrFullscreen(false);

        if (wakeLockSentinelRef.current) {
            try {
                await wakeLockSentinelRef.current.release();
                console.log('Screen wake lock released.');
            } catch (err) {
                console.error(`Wake Lock release error: ${err.name}, ${err.message}`);
            } finally {
                wakeLockSentinelRef.current = null;
            }
        }
    };

    const formattedDailyPin = () => {
        return box?.dailyPin.replace(/(\d{3})(\d{3})(\d{2})/, '$1 $2 $3');
    }

    useEffect(() => {
        return () => {
            if (wakeLockSentinelRef.current) {
                try {
                    wakeLockSentinelRef.current.release()
                        .then(() => console.log('Screen wake lock released on unmount.'))
                        .catch(err => console.error(`Wake Lock release error on unmount: ${err.name}, ${err.message}`))
                        .finally(() => {
                            wakeLockSentinelRef.current = null;
                        });
                } catch(err) {
                    console.error(`Sync Wake Lock release error on unmount: ${err.name}, ${err.message}`);
                    wakeLockSentinelRef.current = null;
                }
            }
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible' && isQrFullscreen && !wakeLockSentinelRef.current) {
                console.log('Document visible and fullscreen QR active, attempting to re-acquire wake lock.');
                setTimeout(handleQrClick, 100);
            } else if (document.visibilityState !== 'visible' && wakeLockSentinelRef.current) {
                console.log('Document hidden, wake lock may be released by browser.');
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('fullscreenchange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('fullscreenchange', handleVisibilityChange);
        };
    }, [isQrFullscreen]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isQrFullscreen && event.key === 'Escape') {
                handleCloseFullscreenQr();
            }
        };

        if (isQrFullscreen) {
            window.addEventListener('keydown', handleKeyDown);
        } else {
            window.removeEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isQrFullscreen]);


    return (
        <Grid item xs={12}>
            {box && (
                <>
                    <Line>
                        <span>Obsadenosť: <b>{box.occupancy ?? 'N/A'}%</b></span>
                    </Line>

                    {children}

                    {showPin && (
                        <Line>
                            <span>Pin na prihlásenie do boxu: <b>{formattedDailyPin() || 'N/A'}</b></span>
                        </Line>
                    )}

                    {showPin && box.dailyPin && (
                        <Line>
                            <div
                                className={classes.qrCodeContainer}
                                onClick={handleQrClick}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleQrClick(); }}
                                aria-label="Show QR code fullscreen"
                            >
                                <div className={classes.qrCodeInner}>
                                    <QRCode
                                        size={256}
                                        style={{ height: "auto", maxWidth: "100%", width: "100%", display: 'block' }}
                                        value={box.dailyPin}
                                        viewBox={`0 0 256 256`}
                                        bgColor="#FFFFFF"
                                        fgColor="#000000"
                                    />
                                </div>
                            </div>
                        </Line>
                    )}

                    <Line>
                        <span>Názov: <b>{box.name || 'N/A'}</b></span>
                    </Line>
                    <Line>
                        <span>Adresa:
                            {/* Make address clickable only if coordinates exist */}
                            {box.location?.latitude && box.location?.longitude ? (
                                <A
                                    className={clsx(classes.href, classes.done)}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        handleAddressClick({
                                            lat: box.location.latitude,
                                            lng: box.location.longitude,
                                        });
                                    }}
                                >
                                    <b>{box.street || ''} {box.city || ''}</b>
                                </A>
                            ) : (
                                <b>{box.street || ''} {box.city || ''}</b> // Render non-clickable if no coords
                            )}
                        </span>
                    </Line>
                    <Line>
                        <span>Popis: <b>{box.description || 'N/A'}</b></span>
                    </Line>

                    {box.photoHref && (
                        <Line>
                            <img src={box.photoHref} alt={box.name || 'box'} style={{
                                maxWidth: '100%',
                                height: 'auto',
                                display: 'block',
                            }}/>
                        </Line>
                    )}
                </>
            )}

            {isMapSelectorOpen && (
                <MapSelector
                    open={isMapSelectorOpen}
                    onClose={handleMapSelectorClose}
                    lat={selectedLocation.lat}
                    lng={selectedLocation.lng}
                />
            )}

            {isQrFullscreen && box?.dailyPin && (
                <div
                    className={classes.fullscreenOverlay}
                    onClick={handleCloseFullscreenQr}
                    role="dialog"
                    aria-modal="true"
                    aria-label="QR Code Fullscreen"
                >
                    <SwipeableViews>
                        <div className={classes.fullscreenQrCodeWrapper}
                             onClick={handleCloseFullscreenQr}
                        >
                            <QRCode
                                style={{
                                    height: "auto",
                                    width: "100%",
                                    maxWidth: "100%",
                                    display: 'block'
                                }}
                                value={box.dailyPin}
                                viewBox={`0 0 256 256`}
                                bgColor="#FFFFFF"
                                fgColor="#000000"
                                level="H"
                            />
                        </div>
                        <div className={classes.fullscreenQrCodeWrapper}
                             onClick={handleCloseFullscreenQr}
                        >
                            <Barcode value={box.dailyPin} height={250} width={4} displayValue="false" />
                        </div>
                    </SwipeableViews>
                    <p className={classes.fullscreenHint}>
                        <big><b>{formattedDailyPin() || 'N/A'}</b></big> <br/>
                        Posun do boku pre čiarový kód v prípade problému s QR kódom.<br/>
                        Pre lepšie skenovanie zvýš jas na telefóne.<br/>
                        Klikni pre zavretie.
                    </p>
                </div>
            )}
        </Grid>
    );
}